import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    WorkOrder,
    WorkOrderWithBuilding,
    WorkOrderStatus,
    WorkOrderPriority,
    CompletionData,
    getAssignees,
    isUnassigned
} from '../../types/workOrders';
import { workOrdersApi } from '../../api/workOrdersApi';
import { isWorkOrderOverdue } from '../../Helpers/WorkOrderUtils';

interface UseWorkOrdersProps {
    buildingKey?: string;
    showArchive?: boolean;
    archiveYear?: number;
}

interface UseWorkOrdersFilters {
    buildingKey?: string;
    priority?: WorkOrderPriority | 'all';
    status?: WorkOrderStatus | 'all';
    assignedTo?: string | 'all' | 'unassigned';
    overdueOnly?: boolean;
}

interface UseWorkOrdersReturn {
    // Data
    workOrders: WorkOrderWithBuilding[];
    loading: boolean;
    error: string | null;
    submitting: boolean;

    // Filtered data
    filteredWorkOrders: WorkOrderWithBuilding[];
    overdueWorkOrders: WorkOrderWithBuilding[];

    // Filter state
    filters: UseWorkOrdersFilters;
    setFilters: (filters: UseWorkOrdersFilters) => void;

    // Admin actions
    createWorkOrder: (data: Omit<WorkOrder, 'key' | 'createdBy' | 'createdAt' | 'status'>) => Promise<string>;
    updateWorkOrder: (key: string, data: Partial<WorkOrder>) => Promise<void>;
    addWorkOrderAssignee: (key: string, userId: string, sendEmail?: boolean) => Promise<void>;
    removeWorkOrderAssignee: (key: string, userId: string) => Promise<void>;
    updateWorkOrderDueDate: (key: string, dueDate: string | null) => Promise<void>;
    cancelWorkOrder: (key: string) => Promise<void>;

    // Completion
    completeWorkOrder: (key: string, data: CompletionData) => Promise<void>;

    // Utilities
    reload: () => Promise<void>;
    clearError: () => void;
}

export const useWorkOrders = ({
    buildingKey,
    showArchive = false,
    archiveYear
}: UseWorkOrdersProps = {}): UseWorkOrdersReturn => {
    const [workOrders, setWorkOrders] = useState<WorkOrderWithBuilding[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const [filters, setFilters] = useState<UseWorkOrdersFilters>({
        priority: 'all',
        status: 'all',
        assignedTo: 'all',
        overdueOnly: false
    });

    // Load work orders
    const loadWorkOrders = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            let data: WorkOrderWithBuilding[];

            if (showArchive && archiveYear) {
                data = await workOrdersApi.getArchiveByYear(archiveYear);
            } else if (buildingKey) {
                // Get by building and enrich with building name
                const orders = await workOrdersApi.getByBuilding(buildingKey);
                data = orders.map(o => ({ ...o, buildingName: '' })) as WorkOrderWithBuilding[];
            } else {
                data = await workOrdersApi.getAll();
            }

            setWorkOrders(data);
        } catch (err) {
            console.error('Error loading work orders:', err);
            setError('Kunde inte ladda ärenden');
        } finally {
            setLoading(false);
        }
    }, [buildingKey, showArchive, archiveYear]);

    // Load on mount or when context changes
    useEffect(() => {
        loadWorkOrders();
    }, [loadWorkOrders]);

    // Compute overdue work orders
    const overdueWorkOrders = useMemo(() =>
        workOrders.filter(isWorkOrderOverdue),
        [workOrders]
    );

    // Filter work orders
    const filteredWorkOrders = useMemo(() => {
        let result = [...workOrders];

        // Filter by building
        if (filters.buildingKey) {
            result = result.filter(wo => wo.buildingKey === filters.buildingKey);
        }

        // Filter by priority
        if (filters.priority && filters.priority !== 'all') {
            result = result.filter(wo => wo.priority === filters.priority);
        }

        // Filter by status
        if (filters.status && filters.status !== 'all') {
            result = result.filter(wo => wo.status === filters.status);
        }

        // Filter by assignee (multi-assignee: membership via the normalizer).
        // "unassigned" means no internal assignees AND no external delegates.
        if (filters.assignedTo) {
            if (filters.assignedTo === 'unassigned') {
                result = result.filter(wo => isUnassigned(wo));
            } else if (filters.assignedTo !== 'all') {
                const uid = filters.assignedTo;
                result = result.filter(wo => uid in getAssignees(wo));
            }
        }

        // Filter overdue only
        if (filters.overdueOnly) {
            result = result.filter(isWorkOrderOverdue);
        }

        // Sort by priority (high first) then due date (soonest first)
        result.sort((a, b) => {
            // Priority order: HIGH > MEDIUM > LOW
            const priorityOrder = { [WorkOrderPriority.HIGH]: 0, [WorkOrderPriority.MEDIUM]: 1, [WorkOrderPriority.LOW]: 2 };
            const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
            if (priorityDiff !== 0) return priorityDiff;

            // Then by due date (nulls last)
            if (!a.dueDate && !b.dueDate) return 0;
            if (!a.dueDate) return 1;
            if (!b.dueDate) return -1;
            return a.dueDate.localeCompare(b.dueDate);
        });

        return result;
    }, [workOrders, filters]);

    // Create work order
    const createWorkOrder = useCallback(async (
        data: Omit<WorkOrder, 'key' | 'createdBy' | 'createdAt' | 'status'>
    ): Promise<string> => {
        setSubmitting(true);
        setError(null);

        try {
            const key = await workOrdersApi.create(data);
            await loadWorkOrders();
            return key;
        } catch (err: any) {
            console.error('Error creating work order:', err);
            const message = err.message || 'Kunde inte skapa ärende';
            setError(message);
            throw new Error(message);
        } finally {
            setSubmitting(false);
        }
    }, [loadWorkOrders]);

    // Update work order
    const updateWorkOrder = useCallback(async (key: string, data: Partial<WorkOrder>): Promise<void> => {
        setSubmitting(true);
        setError(null);

        try {
            await workOrdersApi.update(key, data);
            await loadWorkOrders();
        } catch (err: any) {
            console.error('Error updating work order:', err);
            const message = err.message || 'Kunde inte uppdatera ärende';
            setError(message);
            throw new Error(message);
        } finally {
            setSubmitting(false);
        }
    }, [loadWorkOrders]);

    // Add/remove a single internal assignee (multi-assignee) without disturbing
    // the others or any external delegate.
    const addWorkOrderAssignee = useCallback(async (key: string, userId: string, sendEmail: boolean = true): Promise<void> => {
        setSubmitting(true);
        setError(null);
        try {
            await workOrdersApi.addAssignee(key, userId, sendEmail);
            await loadWorkOrders();
        } catch (err: any) {
            const message = err.message || 'Kunde inte lägga till tilldelad';
            setError(message);
            throw new Error(message);
        } finally {
            setSubmitting(false);
        }
    }, [loadWorkOrders]);

    const removeWorkOrderAssignee = useCallback(async (key: string, userId: string): Promise<void> => {
        setSubmitting(true);
        setError(null);
        try {
            await workOrdersApi.removeAssignee(key, userId);
            await loadWorkOrders();
        } catch (err: any) {
            const message = err.message || 'Kunde inte ta bort tilldelad';
            setError(message);
            throw new Error(message);
        } finally {
            setSubmitting(false);
        }
    }, [loadWorkOrders]);

    // Update due date (pass null to clear)
    const updateWorkOrderDueDate = useCallback(async (key: string, dueDate: string | null): Promise<void> => {
        setSubmitting(true);
        setError(null);
        try {
            await workOrdersApi.updateDueDate(key, dueDate);
            await loadWorkOrders();
        } catch (err: any) {
            console.error('Error updating work order due date:', err);
            const message = err.message || 'Kunde inte uppdatera slutdatum';
            setError(message);
            throw new Error(message);
        } finally {
            setSubmitting(false);
        }
    }, [loadWorkOrders]);

    // Cancel work order
    const cancelWorkOrder = useCallback(async (key: string): Promise<void> => {
        setSubmitting(true);
        setError(null);

        try {
            await workOrdersApi.cancel(key);
            await loadWorkOrders();
        } catch (err: any) {
            console.error('Error cancelling work order:', err);
            const message = err.message || 'Kunde inte avbryta ärende';
            setError(message);
            throw new Error(message);
        } finally {
            setSubmitting(false);
        }
    }, [loadWorkOrders]);

    // Complete work order (via Cloud Function)
    const completeWorkOrder = useCallback(async (key: string, data: CompletionData): Promise<void> => {
        setSubmitting(true);
        setError(null);

        try {
            const workOrder = workOrders.find((w) => w.key === key);
            await workOrdersApi.complete(key, data, { buildingKey: workOrder?.buildingKey, title: workOrder?.title });
            await loadWorkOrders();
        } catch (err: any) {
            console.error('Error completing work order:', err);
            const message = err.message || 'Kunde inte slutföra ärende';
            setError(message);
            throw new Error(message);
        } finally {
            setSubmitting(false);
        }
    }, [loadWorkOrders, workOrders]);

    // Clear error
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        workOrders,
        loading,
        error,
        submitting,
        filteredWorkOrders,
        overdueWorkOrders,
        filters,
        setFilters,
        createWorkOrder,
        updateWorkOrder,
        addWorkOrderAssignee,
        removeWorkOrderAssignee,
        updateWorkOrderDueDate,
        cancelWorkOrder,
        completeWorkOrder,
        reload: loadWorkOrders,
        clearError
    };
};
